/* prettier-ignore */

import { Component, computed, DestroyRef, effect, inject, signal, ViewEncapsulation } from "@angular/core";
import { resolveEmbedParams } from "./pages/chatbot-embed/chatbot-embed-params";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { timer } from "rxjs";
import { ToolsService } from "./services/tools.service";
import { ActivatedRouteSnapshot, NavigationEnd, Router, RouterOutlet } from "@angular/router";
import { NavigationService } from "./services/navigation.service";
import { RestService } from "./services/rest.service";
import { AuthService } from "./auth/services";
import { ModalController } from "@ionic/angular";
import { UserService } from "./user/services";
import { StatisticsService } from "./services/statistics.service";
import { fadeInAnimation } from './route-animations';
import { Location } from '@angular/common';
import { CookieService } from "ngx-cookie-service";
import { DebugService } from "./services/debug.service";
import { ChatBotScriptService } from "./services/chatbot.service";
import { NewCustomRouteService } from "./pages/new-custom-route/new-custom-route.service";
import { BreakpointsService } from "./services/breakpoints.service";
import { GamificationService } from "./shared/gamification/services/gamification.service";
import { Passport } from "./shared/gamification/interfaces/passport.interface";
import { EmbedContextService, GeolocationService, LocalStorageService, OfflineDetectionService, Snackbar, TranslationService, WebEntryTrackingService } from "./services";
import { MatDialogService } from "./services/mat-dialog.service";
import { PassportPopUpComponent } from "./shared/gamification/components/popup/popup.component";
import { UserUpdatedResponse } from "./user/interfaces";
import { INFORMER_MODE_STORAGE_KEY, INFORMER_REGISTER_STORAGE_KEY, toInformerMode, toInformerRegisterId } from "./interfaces";
import { DataChatService } from "src/chatbot/service/dataChat.service";
import { isWebViewUserAgent, sanitizeShareBase } from "./helpers";
import { SHARE_BASE_ALLOWED_HOSTS } from "./config/share-link.config";
import { ShareRouteLinkService } from "./pages/custom-route/services/share-route-link.service";
import { CustomRouteService } from "./pages/custom-route/services/custom-route.service";
import { PlannerModeService } from "./services/planner-mode.service";
import { PostLoginRedirect } from "./auth/services/post-login-redirect.service";
import '@iurban-malaga/cicerone-ui';

@Component({
  selector: "app-root",
  templateUrl: "app.component.html",
  styleUrls: ["app.component.scss"],
  encapsulation: ViewEncapsulation.None,
  animations: [ fadeInAnimation ] 
})

export class AppComponent {
  private dataChat = inject(DataChatService);
  private debugService = inject(DebugService);
  private customRouteService = inject(NewCustomRouteService);
  private _gamificationService = inject(GamificationService);
  private _breakpointsService = inject(BreakpointsService);
  private _localStorageService = inject(LocalStorageService);
  private _matDialogService = inject(MatDialogService);
  private _translationService = inject(TranslationService);
  private _snackbarService = inject(Snackbar);
  private _offlineDetectionService = inject(OfflineDetectionService);
  private _embedContext = inject(EmbedContextService);
  private _geolocationService = inject(GeolocationService);
  private _webEntryTracking = inject(WebEntryTrackingService);
  private _shareRouteLinkService = inject(ShareRouteLinkService);
  private _plannerMode = inject(PlannerModeService);
  // HRS-3482: instanciado desde el arranque para que, al volver del login social
  // (recarga completa), restaure el destino pendiente y navegue al haber sesión.
  private _postLoginRedirect = inject(PostLoginRedirect);
  /** Token del planner v3 (distinto de NewCustomRouteService, el planner legacy). */
  private _customRouteTokenService = inject(CustomRouteService);

  public isRegisterMode = this.tools.isRegisterMode;
  public isPlannerMode = this.tools.isPlannerMode;
  public isAppLoaded = this.tools.isAppLoaded;
  public isTechMode = this.tools.isTechMode;
  private isInfoRoutePage = this.tools.isInfoRoutePage;
  private _passportPopup = this._gamificationService.passportPopup;
  private _passportBottomSheet = this._gamificationService.passportBottomSheet;
  protected isMobile = this._breakpointsService.isMobile;
  public hideHeaderOnCurrentRoute = signal(false);
  public isOnline = this._offlineDetectionService.isOnline;

  public showPassport = computed(() => this._passportPopup() || this._passportBottomSheet());

  private _isLoggedOut = this.authService.isUserLoggedOut;
  private _logoutEffectInitialized = false;
  private _onLogout = effect(() => {
    const isLoggedOut = this._isLoggedOut();
    if (!this._logoutEffectInitialized) { this._logoutEffectInitialized = true; return; }
    if (isLoggedOut) {
      this._snackbarService.showSuccessSnackbar('Sesión cerrada con éxito');
    }
  });

  /**
   * Embed widget: resolve the tenant + DEVICE only ONCE we actually have a chatbotId.
   * The id arrives either from `?id=` (set synchronously in ngOnInit) or — in the real
   * CMS embed — from the parent's postMessage handshake (ChatbotEmbedComponent sets the
   * shared signal). We must NOT call getCommonApp with the hostname-derived subdomain
   * (`checkWebApp` sets it from the URL → e.g. `chatbot-planner`), which 404s and lands
   * on not-found. So we wait here for a real chatbotId → tenant before loading the device.
   */
  private _embedDeviceResolved = false;
  private _resolveEmbedDeviceOnChatbotId = effect(() => {
    if (!this._embedContext.isEmbed() || this._embedDeviceResolved) return;
    const id = this._embedContext.chatbotId();
    if (!id) return;
    const params = new URLSearchParams(window.location.search);
    // Tenant resolution order: explicit ?tenant=/?subdomain= override → id→tenant map →
    // the subdomain already derived from the host (e.g. fuengirola.ciceroneweb.com → "fuengirola").
    // The host fallback is what makes the planner/route DEVICE resolve for chatbots NOT in the map
    // (the faithful CMS handshake passes no tenant), exactly like the normal site resolves by subdomain.
    const tenant =
      params.get('tenant') ||
      params.get('subdomain') ||
      resolveEmbedParams(id)?.tenant ||
      this.rest.subDomain;
    if (!tenant) return; // can't resolve the tenant yet — wait
    this._embedDeviceResolved = true;
    this.rest.subDomain = tenant;
    this._loadEmbedDevice();
  });
  
  queryParamLang: any;
  protected chatId = this.chatService.chatId;

  private _destroyRef = inject(DestroyRef);
  
  private _userDataLocalStorage: UserUpdatedResponse | null = this._localStorageService.getItem('user') ?? null; // TODO: DELETE FOR SSR -> NEW LOGIN STRATEGY (SECURE COOKIE)
  constructor(
    public modalCtrl: ModalController,
    private router: Router,
    public tools: ToolsService,
    public rest: RestService,
    public navigation: NavigationService,
    public authService: AuthService,
    public userService: UserService,
    public statsService: StatisticsService,
    private cookieService: CookieService,
    private chatService: ChatBotScriptService,
    private location: Location
  ) {
    // Init logged user
    if (this._userDataLocalStorage) { // TODO: DELETE FOR SSR -> NEW LOGIN STRATEGY (SECURE COOKIE)
      this.authService.setUser(this._userDataLocalStorage);
    } else {
      this.authService.setUser(null);
    }
    
      this.checkWebApp();
      
      // ! PENSAR COMO HACERLO ESCALABLE
       const currentUrl = window.location.href;
       if(currentUrl.includes('turismo.yecla.es')) {
         this.rest.subDomain = 'turismoyecla'
       } else {
         this.rest.subDomain = this.tools.getSubdomain(window.location.href);
       }

      // Social login (HRS-2514): si venimos de la redirección del back con
      // ?social_login_code=..., canjearlo por la sesión antes de que arranque el router.
      this.authService.handleSocialLoginReturn();

      this.debugService.info(this.rest.subDomain, '[AppComponent] SUBDOMAIN');
      this.checkPreProduction();
      this.navigation.startSaveHistory();
      this.tools.setCorporiveColors();
      this.watchHeaderVisibilityByRoute();
      this.keepPlannerModeAliveWhileBrowsing();
      // Offline routes are a WebView-only feature: only wire the offline->/offline-routes
      // redirect when running inside the native WebView (never in a browser).
      if (this.tools.isWebApp())
        this._offlineDetectionService.start();
  }

  ngOnInit(): void {
    if (window.location?.pathname?.includes('survey-id'))
      return;

    this._geolocationService.prefetch();

    // HRS-2890: share_base (URL del planner en la web anfitriona, base del
    // enlace de compartir) llega en el src del iframe tanto en el widget del
    // chatbot como en el site embebido en modo planner. Se resuelve AQUÍ,
    // antes del branch embed (que retorna sin pasar por initializeParams),
    // para que toda la app lo tenga con una sola lectura.
    this._resolveShareBase();
    // Ídem shared_route: se consume aquí para ambos modos (la rama embed de
    // abajo retorna sin pasar por initializeParams).
    this._openSharedRouteIfPresent();

    // Embedded chatbot widget: resolve the DEVICE for the tenant passed as
    // ?tenant= (the planner/route backend needs device().client/type/app_id),
    // but never redirect to not-found and skip the rest of the site init.
    if (this._embedContext.isEmbed()) {
      // El chatbot usa statsService.sessionId como token; inicializarlo aquí
      // (en la rama normal lo hace checkToken/setSessionId) o el WS y el
      // historial irían con user_id=undefined.
      this.statsService.setSessionId();
      this.checkToken();

      // El chatbotId puede venir en la URL (?id=) ya, o llegar luego por postMessage
      // (el script de embed real del CMS NO pone query params). En ambos casos,
      // _resolveEmbedDeviceOnChatbotId resuelve tenant + device cuando hay id; aquí solo
      // sembramos el id si está en la URL. NO llamamos a getCommonApp con el hostname.
      const idFromUrl = new URLSearchParams(window.location.search).get('id');
      if (idFromUrl) this._embedContext.chatbotId.set(idFromUrl);
      return;
    }

    const queryParams = window.location.search ? window.location.search : "";
    const params = new URLSearchParams(queryParams);
    const loadAppFromParam = this.getQueryParam(params);
    this.initializeParams(loadAppFromParam);
    this.checkPlannerModeEnabled();
    this.checkInformerMode();
    this.checkFromDistributor();
    this.checkToken();

    if (this.rest.subDomain) {
      this.rest.getCommonApp(this.statsService.token).subscribe({
        next: (response) => {
        this.debugService.debug(response, '[AppComponent] Common API Response');

        this.checkCanUseRoutes(response);
        this.tools.setCommonAppReady();

        if(this.isRegisterMode()){
          this.setRegisterMode(response);
          return;
        }

        if(this.isPlannerMode()){
          this.setPlannerMode(response);
          return;
        }

        if (this.router.url.includes("survey-id"))
          return;

        if (response.error) {
          this.router.navigateByUrl("not-found");
          return;
        }

        this.enablePassportAndSetDataIfExist(response.passport);
        this.showPassportPopUpIfNeeded();
        this.checkPolylinesActive(response?.location?.visualizeLines);
        this.checkFooter(response?.appStyle?.logoList?.footerLogo);
        this.checkRRSS(response);

        this.rest.setDevice(response);
        this.setRouteButtons(response);
        this.debugService.debug(this.rest.device(), '[AppComponent] Device Info');
        this.setLang();
        this._webEntryTracking.trackEntryIfNeeded();

        if(this.checkInfoRoute(response))
          return;

        if (response?.type === 'app'){
          this.userService.checkAllUserEntries();
          this.tools.setIsApp(true);
          this.tools.setAppStarted()
        }
          
        if (response?.type === 'distributor'){
          this.tools.setIsDistributor(true);
          this.tools.setAppStarted()
        }
  
        if (!this.router.url.includes('tech-edit')){
          this.enableChat(response)
        }

        },
        error: () => {
          this.tools.setCommonAppReady();
          this.router.navigateByUrl("not-found");
        }
      })
      return;
    }
    this.tools.setCommonAppReady();
    this.router.navigateByUrl("not-found");
  }

  /**
   * Embed-only device resolution. Called by _resolveEmbedDeviceOnChatbotId once the tenant
   * is known. Resolves the DEVICE (planner/route backend needs device().client/type/app_id)
   * + capabilities (audio/image/etc. like enableChat() in the normal branch), but never
   * redirects to not-found: the chat must keep working even if the device fails.
   */
  private _loadEmbedDevice() {
    this.rest.getCommonApp(this.statsService.token).subscribe((response) => {
      if (!response || response.error) return; // degrade gracefully (chat works, no route)
      this.rest.setDevice(response);
      this.setRouteButtons(response);
      this.setLang();
      this._webEntryTracking.trackEntryIfNeeded();
      if (response?.type === 'app') { this.tools.setIsApp(true); this.tools.setAppStarted(); }
      if (response?.type === 'distributor') { this.tools.setIsDistributor(true); this.tools.setAppStarted(); }

      // Capabilities (audio/imagen/etc.) como en enableChat() de la rama normal.
      const caps = response?.chatbot?.config?.capabilities;
      if (typeof caps?.image === 'boolean') this.dataChat.allowSendImage.set(caps.image);
      if (typeof caps?.voice === 'boolean') this.dataChat.allowSendAudio.set(caps.voice);
      if (typeof caps?.proactive === 'boolean') this.dataChat.allowProactiveBubbles.set(caps.proactive);
      if (typeof caps?.multi_items_modal === 'boolean') this.dataChat.allowShowMultiItemsModal.set(caps.multi_items_modal);
      this.dataChat.checkCookies();
    });
  }

  prepareRoute(outlet: RouterOutlet) {
    return outlet && outlet.activatedRouteData && outlet.activatedRouteData['animation'];
  }

  increaseClick() {
    if (!this.tools.showHeader()) 
      return;

    this.statsService.sumClicks();
    this.statsService.setLocalStorageSessionIDFromClick();
    this.statsService.setLocalStorageTokenFromClick();
  }

  private watchHeaderVisibilityByRoute() {
    this.updateHeaderVisibilityByRoute();

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd)
        this.updateHeaderVisibilityByRoute();
    });
  }

  private updateHeaderVisibilityByRoute() {
    let currentRoute: ActivatedRouteSnapshot = this.router.routerState.snapshot.root;

    while (currentRoute.firstChild)
      currentRoute = currentRoute.firstChild;

    this.hideHeaderOnCurrentRoute.set(!!currentRoute.data?.['hikingIframe'] || !!currentRoute.data?.['chatbotEmbed']);
    this.tools.isHikingStandalone.set(!!currentRoute.data?.['hikingIframe']);
  }

  getQueryParam(queryParam: URLSearchParams) {
    let params: any = {};

    if (queryParam.get("lang")) 
      params["lang"] = queryParam.get("lang");

    if (queryParam.get("distributor"))  
      params["distributor"] = queryParam.get("distributor");
    
    if (queryParam.get("token")) 
      params["token"] = queryParam.get("token");
    
    if (queryParam.get("id")) 
      params["id"] = queryParam.get("id");
    
    // HRS-3145: `techMode`, `informer` y `register` ya no se leen aquí. Los trata
    // el router (`legacyInformerParamsGuard` → `/tech-mode/...`), que es quien
    // activa el modo del informador.

    if (queryParam.get("showDebug")) 
      params["showDebug"] = queryParam.get("showDebug");

    if (queryParam.get("passport")) 
      params["passport"] = queryParam.get("passport");
    
    if (queryParam.get("enableMic")) 
      params["enableMic"] = queryParam.get("enableMic");
    
    if (queryParam.get("planner"))
      params["planner"] = queryParam.get("planner");

    return Object.keys(params).length > 0 ? params : "";
  }

  /**
   * HRS-2890: valida `share_base` contra la allowlist global y lo publica en
   * ShareRouteLinkService para el enlace de compartir del planner. El valor
   * llega YA decodificado (`URLSearchParams.get`); si no valida queda null y
   * el enlace cae al fallback por idioma (embed) o al default del site.
   */
  private _resolveShareBase(): void {
    const raw = new URLSearchParams(window.location.search).get('share_base');
    if (!raw) return;
    this._shareRouteLinkService.shareBaseUrl.set(sanitizeShareBase(raw, SHARE_BASE_ALLOWED_HOSTS));
  }

  /**
   * HRS-2890: consumo de `?shared_route=<token>` (enlace compartido abierto en
   * la web anfitriona). Centralizado aquí para AMBOS modos — chatbot-embed no
   * lee este param — con acción distinta por modo:
   * - Widget del chatbot: montar el planner in-place seteando el token (mismo
   *   camino que el token que genera el chat; navegar sacaría al usuario del
   *   layout del widget) y limpiar el param de la URL interna del iframe. El
   *   effect de CustomRouteComponent dispara la carga cuando el device
   *   resuelve; si el token no vale, avisa y vuelve al chat.
   * - Site (modo planner o navegación normal): navegar al deep-link existente
   *   `/custom-route/v3/<token>` con `replaceUrl` (la URL con el param no
   *   queda en el historial). CanUseRoutesGuard retiene la navegación hasta
   *   que getCommonApp resuelve, igual que en un deep-link directo; si el
   *   token no vale, custom-route.component avisa con un dialog y vuelve a la home.
   */
  private _openSharedRouteIfPresent(): void {
    const token = new URLSearchParams(window.location.search).get('shared_route')?.trim();
    if (!token) return;

    if (this._embedContext.isEmbed()) {
      this._shareRouteLinkService.sharedRouteToken.set(token);
      this._customRouteTokenService.setToken(token);
      this._cleanSharedRouteParam();
      return;
    }

    this.router.navigateByUrl(`/custom-route/v3/${encodeURIComponent(token)}`, { replaceUrl: true });
  }

  /**
   * Quita SOLO `shared_route` de la URL interna del iframe (trigger one-shot:
   * no debe contaminar enlaces construidos desde window.location.href).
   * `id`, `lang` y `share_base` se conservan (debug + relecturas del embed).
   * Mismo patrón que auth.service, sin pasar por el Router.
   */
  private _cleanSharedRouteParam(): void {
    const params = new URLSearchParams(window.location.search);
    params.delete('shared_route');
    const query = params.toString();
    history.replaceState(null, '', window.location.pathname + (query ? `?${query}` : '') + window.location.hash);
  }

  initializeParams(params: any) {
    this.debugService.info(params, '[AppComponent] Params');
    this.tools.homeDistributor = null;

    if (params["showDebug"]) {
      this.debugService.enableLogs();
    }

    if (params["distributor"]) {
      this.tools.homeDistributor =  this.tools.getDistributorUrl(params["distributor"]);
      this.tools.setWithExpiry('homeDistributor', this.tools.homeDistributor);
    };

    if (params["token"]) {
      this.statsService.token = params["token"];
      this.rest.token = params["token"];
      this.debugService.warn('Setting new token', '[AppComponent]');
      this.tools.setWithExpiry('token', params['token']);
    }

    if (params["lang"]) {
      this.queryParamLang = params["lang"];
    }

    if (params["planner"]) {
      this.debugService.info('Enabling Planner Mode', '[AppComponent]');
      this._plannerMode.enable();
      this.tools.isPlannerMode.set(true);
    }

    this.cleanConsumedQueryParams();
    this.statsService.setSessionId();
    if (!params["token"]) 
      this.statsService.setToken();
  }

  // Limpia de la URL solo los params consumidos aquí, preservando la ruta y el resto
  // de query params (p.ej. tags/sort de los listados, que llegan por QR de canales)
  private cleanConsumedQueryParams() {
    // HRS-3145: `techMode`, `informer` y `register` no se limpian aquí. Este
    // `replaceState` corre en el `ngOnInit`, ANTES de la navegación inicial del
    // router, así que lo que se borre aquí no lo ve ningún guard. Los consume
    // (y los quita de la URL al redirigir) `legacyInformerParamsGuard`.
    const consumedParams = ["lang", "distributor", "token", "showDebug", "planner"];
    const params = new URLSearchParams(window.location.search);
    if (!consumedParams.some((param) => params.has(param)))
      return;

    consumedParams.forEach((param) => params.delete(param));
    this.location.replaceState(this.location.path().split('?')[0], params.toString());
  }

  checkPlannerModeEnabled(){
    if (!this._plannerMode.isEnabled())
      return;

    // T10797: volver a entrar cuenta como uso, así que el plazo empieza de nuevo.
    this._plannerMode.enable();
    this.isPlannerMode.set(true)
  }

  /**
   * T10797: mientras se navega, el modo planificador no caduca. Los 10 minutos
   * se cuentan desde la última pantalla, de modo que el navegador que se queda
   * abierto sin nadie delante vuelve solo al modo normal.
   */
  private keepPlannerModeAliveWhileBrowsing() {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd)
        this._plannerMode.keepAlive();
    });
  }

  checkPreProduction(){
    const preProduction =  this.cookieService.get('preProduction');
    if (!preProduction) 
      return;

    this.rest.subDomain = preProduction.trim();
    this.rest.apiUrlBase = 'https://pre.ciceroneweb.com';
    this.tools.isPreProduction.set(true);
  }

  /**
   * HRS-2990: restaura el modo del informador de una entrada anterior (la ruta
   * `/tech-mode/...` redirige sin el prefijo, así que al navegar solo queda lo
   * persistido). `techMode` sigue leyéndose para las sesiones que ya lo tenían
   * guardado.
   *
   * Ya no se cierra la sesión al entrar: el informador debe quedar identificado
   * (autologin del ticket). Antes se llamaba a `customLogOut()` + `resetAll()`,
   * que expulsaba justo al usuario que se quiere atribuir.
   */
  checkInformerMode(){
    const storedMode = toInformerMode(this.tools.getWithExpiry(INFORMER_MODE_STORAGE_KEY));
    const legacyTechMode = this.tools.getWithExpiry('techMode');
    const informerMode = storedMode ?? (legacyTechMode ? 'predefined-route' : null);
    if (!informerMode)
      return;

    this.tools.informerMode.set(informerMode);

    // El registro elegido tiene que sobrevivir a la navegación por la web: el
    // técnico entra en la home, pasea por los contenidos y abre el formulario
    // después, cuando el query param ya se consumió.
    const registerId = toInformerRegisterId(this.tools.getWithExpiry(INFORMER_REGISTER_STORAGE_KEY));
    if (registerId)
      this.tools.informerRegisterId.set(registerId);
  }

  checkFromDistributor(){
    const fromDistributor = this.tools.getWithExpiry('homeDistributor');
    if (fromDistributor)
      this.tools.homeDistributor = fromDistributor
  }

  checkToken(){
    this.debugService.info('Checking token', '[AppComponent]');
    if (this.statsService.token)
      return;

    const token = this.tools.getWithExpiry("token");
    if (token) {
      this.debugService.info('Restoring Found Token', '[AppComponent]');
      this.statsService.token = token;
      this.rest.token = token;
      this.tools.setWithExpiry("token", token);
    } 
    else 
      this.debugService.warn('Token NOT Found', '[AppComponent]');
  }

  checkFooter(isFooter: any){
    if (isFooter)
      this.tools.isFooter.set(true);
  }

  checkPolylinesActive(visualizeLines){
    this.customRouteService.showPolylines.set(visualizeLines ?? true)
  }

  checkInfoRoute(response: any){
    if (this.isInfoRoutePage()){
      if (response?.type !== 'app' || response?.type !==  'distributor')
        return false;

      if (response?.type === 'app')
        this.tools.setIsApp(true);

      if (response?.type === 'distributor')
        this.tools.setIsDistributor(true);

      this.tools.setCorporativeColors();
      this.tools.enableHeader();
      return true;
    }
  }

  checkCanUseRoutes(response: any){
    if (response.showPlannerButton === false){
      this.tools.canUseRoutes.set(false);
    }
  }

  setLang(){
    if (this.hideHeaderOnCurrentRoute())
      return;

    this._translationService.initFromDevice(this.queryParamLang);
  }

  setRegisterMode(response: any){
    if (response.error)
      return;

    this.rest.setDevice(response);
    this._webEntryTracking.trackEntryIfNeeded();
    this.tools.setCorporativeColors();
    const mainLang = this.rest.device()?.mainLanguage || "es";
    this._translationService.setTenantLanguages(this.rest.device()?.languages);
    this._translationService.useDictionaryLang(mainLang);
    this.tools.enableHeader();

    // HRS-2990: publicar el tipo de tenant también aquí, como ya hace el modo
    // planner. Sin esto isApp$/isDistributor$ no emiten nunca en las rutas de
    // registro, y todo lo que espera a `waitUntilReady()` —la carga de datos del
    // formulario, la lista de PDFs— se queda esperando indefinidamente (era el
    // motivo del apaño de HRS-2909 en SendUpdateRouteService).
    if (response?.type === 'app')
      this.tools.setIsApp(true);

    if (response?.type === 'distributor')
      this.tools.setIsDistributor(true);

    this.enableChat(response)
  }

  setPlannerMode(response: any){
    if (response.error) {
      this.router.navigateByUrl("not-found");
      return;
    }

    this.rest.setDevice(response);
    this._webEntryTracking.trackEntryIfNeeded();
    this.tools.setCorporativeColors();

    this.tools.disableHeader();
    this.checkPolylinesActive(response?.location?.visualizeLines);
    this.setRouteButtons(response);
    this.customRouteService.initializeData();
    this.setLang();

    // Set app type based on response but keep header disabled
    if (response?.type === 'app'){
      this.tools.setIsApp(true);
    }
      
    if (response?.type === 'distributor'){
      this.tools.setIsDistributor(true);
    }
    
    // Redirect to register-user if not already there
    if (!this.router.url.includes('register-user') && !this.router.url.includes('custom-route')) {
      this.router.navigateByUrl('/register-user');
    }

    if(response?.clientID === 130 && response?.chatbot?.id === 133){
      this.enableChat(response);
    }
  }

  public setRouteButtons(response: any){
    if (response?.showRatings === true || response?.showRatings === false)
      this.tools.showRatingsButton.set(response.showRatings);

    if (response?.shareRoute === true || response?.shareRoute === false)
      this.tools.showShareRouteButton.set(response.shareRoute);

    if (response?.sendRoute === true || response?.sendRoute === false)
      this.tools.showSendRouteButton.set(response.sendRoute);

    if (response?.showPlannerButton === true || response?.showPlannerButton === false)
      this.tools.showPlannerButton.set(response.showPlannerButton);

    // HRS-2579: las apps anteriores al campo no traen el bloque; se quedan con el
    // null del servicio, que el formulario lee como "no se pregunta, 75 km".
    const maxRouteRadius = response?.maxRouteRadius;
    if (maxRouteRadius && typeof maxRouteRadius.enabled === 'boolean')
      this.tools.maxRouteRadius.set({
        enabled: maxRouteRadius.enabled,
        defaultKm: typeof maxRouteRadius.defaultKm === 'number' ? maxRouteRadius.defaultKm : null,
      });
  }

  enableChat(response: any){
    if (this.hideHeaderOnCurrentRoute())
      return;
    
    this.customRouteService.initializeData();
    const chatbotId = response?.chatbot?.id ?? response?.lang?.chatbot;
    if (!chatbotId)
      return;

    this.rest.updateDevice({ chatBotId: chatbotId });
    
    //Check legacy chatbot version (script embebed)
    if (response?.chatbot?.version === 0){
      const browserLang = this._translationService.getBrowserLang();
      const mainLang = this.rest.device()?.mainLanguage || browserLang || "es";
      this.chatService.loadChat(mainLang);
      return;
    }
    
    this.chatService.chatId.set(chatbotId);
    this.dataChat.id.set(String(chatbotId));
    // Re-POST geo ya con chatbot_id + thread_ (Metrics join).
    this._webEntryTracking.rebindToChatThread();
    
    const allowSendImages = response?.chatbot?.config?.capabilities?.image;
    if (allowSendImages === true || allowSendImages === false)
      this.dataChat.allowSendImage.set(allowSendImages);
    
    const allowSendAudio = response?.chatbot?.config?.capabilities?.voice;
    if (allowSendAudio === true || allowSendAudio === false)
      this.dataChat.allowSendAudio.set(allowSendAudio);
    
    const allowProactiveBubbles = response?.chatbot?.config?.capabilities?.proactive;
    if (allowProactiveBubbles === true || allowProactiveBubbles === false)
      this.dataChat.allowProactiveBubbles.set(allowProactiveBubbles);

    // const allowShowMultiItemsModal = response?.chatbot?.config?.capabilities?.multi_items_modal;
    // if (allowShowMultiItemsModal === true || allowShowMultiItemsModal === false)
    //   this.dataChat.allowShowMultiItemsModal.set(allowShowMultiItemsModal);
    
    this.dataChat.checkCookies();
  }

  private checkWebApp(){
    this.tools.isWebApp.set(isWebViewUserAgent());
  }

  private checkRRSS(response: any){
    if (response?.rrssCarousel?.enabled && response?.rrssCarousel?.script !== ''){
      this.tools.rrssUrl.set(response?.rrssCarousel?.script);
    }
  }

  private enablePassportAndSetDataIfExist(passport: Passport){
    if (!passport)
      return;

    if (this.tools.isNonEmptyObject(passport))
      this._gamificationService.setPassportsEnabled();

    if (this.tools.isNonEmptyObject(passport.popup))
      this._gamificationService.setPassportPopup(passport.popup);

    if (this.tools.isNonEmptyObject(passport.bottom_sheet))
      this._gamificationService.setPassportBottomSheet(passport.bottom_sheet);

    if (this.tools.isNonEmptyObject(passport.what_is_it_description))
      this._gamificationService.setWhatIsItDescription(passport.what_is_it_description);

    if (this.tools.isNonEmptyObject(passport.how_it_works))
      this._gamificationService.setHowItWorks(passport.how_it_works);
  }

  private showPassportPopUpIfNeeded(): void {
    const popUpActive = this._localStorageService.getItem<boolean>('pop-up-actived');

    if (popUpActive === false || this.authService.user()) return;

    if (!this._passportPopup()) return;

    timer(5000).pipe(
      takeUntilDestroyed(this._destroyRef)
    ).subscribe(() => {
      this._openPassportPopUp();
    });
  }
  
  private async _openPassportPopUp(): Promise<void> {
    if (this._localStorageService.getItem<boolean>('pop-up-actived') === false)
      return;

    await this._matDialogService.openMatDialog({
      component: PassportPopUpComponent,
      data: this._passportPopup(),
      panelClass: 'passport-popup-dialog',
    });

    this._matDialogService.currentDialogRef().afterClosed().subscribe(() => {
      this._localStorageService.setItem('pop-up-actived', false);
    });
  }

  protected isCustomRoute(): boolean {
    return this.tools.isRoutePage('/custom-route');
  }
}
  


